import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import GoogleLocationPicker from '../components/GoogleLocationPicker';
import { categories } from '../mock';
import { AlertCircle, Loader2, ArrowLeft, X, Plus, Upload } from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EditAd = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { countryConfig } = useCountry();
  const currencySymbol = countryConfig?.currency_symbol || 'Rs';
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    location: '',
    condition: '',
    negotiable: true,
    phone: ''
  });
  const [locationData, setLocationData] = useState({
    latitude: 6.9271,
    longitude: 79.8612,
    address: ''
  });
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: `/edit-ad/${id}`, message: 'Please login to edit your ad' } });
      return;
    }
    fetchListing();
  }, [id, navigate]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/listings/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const listing = response.data;
      
      // Populate form with existing data
      setFormData({
        title: listing.title || '',
        description: listing.description || '',
        category: listing.category || '',
        price: listing.price?.toString() || '',
        location: listing.location || '',
        condition: listing.condition || '',
        negotiable: listing.negotiable !== false,
        phone: listing.phone || ''
      });

      setLocationData({
        latitude: listing.latitude || 6.9271,
        longitude: listing.longitude || 79.8612,
        address: listing.location || ''
      });

      setExistingImages(listing.images || []);
      
    } catch (err) {
      console.error('Error fetching listing:', err);
      if (err.response?.status === 404) {
        setError('Listing not found');
      } else if (err.response?.status === 403) {
        setError('You are not authorized to edit this listing');
      } else {
        setError('Failed to load listing details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveExistingImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to remove this image?')) {
      return;
    }

    try {
      setDeletingImage(imageUrl);
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(
        `${API_URL}/api/listings/${id}/images?image_url=${encodeURIComponent(imageUrl)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setExistingImages(response.data.images);
      }
    } catch (err) {
      console.error('Error removing image:', err);
      alert(err.response?.data?.detail || 'Failed to remove image');
    } finally {
      setDeletingImage(null);
    }
  };

  const handleNewImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + newImages.length + files.length;
    
    if (totalImages > 5) {
      alert(`Maximum 5 images allowed. You have ${existingImages.length} existing and ${newImages.length} new images.`);
      return;
    }

    // Validate file types
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    });

    setNewImages([...newImages, ...validFiles]);
  };

  const handleRemoveNewImage = (index) => {
    setNewImages(newImages.filter((_, i) => i !== index));
  };

  const handleUploadNewImages = async () => {
    if (newImages.length === 0) return;

    try {
      setUploadingImages(true);
      const token = localStorage.getItem('token');
      
      const formDataImages = new FormData();
      newImages.forEach(image => {
        formDataImages.append('images', image);
      });

      const response = await axios.post(
        `${API_URL}/api/listings/${id}/images`,
        formDataImages,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setExistingImages(response.data.images);
        setNewImages([]);
        alert('Images uploaded successfully!');
      }
    } catch (err) {
      console.error('Error uploading images:', err);
      alert(err.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Prepare update data
      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price: parseFloat(formData.price),
        location: locationData.address || formData.location,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        condition: formData.condition,
        negotiable: formData.negotiable,
        phone: formData.phone
      };

      const response = await axios.put(
        `${API_URL}/api/listings/${id}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.message) {
        alert('Ad updated successfully!');
        navigate(`/listing/${id}`);
      }
    } catch (err) {
      console.error('Error updating ad:', err);
      setError(err.response?.data?.detail || 'Failed to update ad. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading listing details...</p>
        </div>
      </div>
    );
  }

  const totalImageCount = existingImages.length + newImages.length;
  const canAddMoreImages = totalImageCount < 5;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4 mb-2">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <CardTitle className="text-3xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Edit Your Ad
                </CardTitle>
                <CardDescription>Update the details of your listing</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Photos Section */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg border">
              <h3 className="text-lg font-semibold mb-4">Photos ({totalImageCount}/5)</h3>
              
              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Current Photos</p>
                  <div className="flex gap-3 flex-wrap">
                    {existingImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <div className="w-24 h-24 rounded-lg overflow-hidden border">
                          <img 
                            src={img.startsWith('http') ? img : `${API_URL}${img}`} 
                            alt={`Image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/96?text=No+Image'; }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingImage(img)}
                          disabled={deletingImage === img}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-md"
                        >
                          {deletingImage === img ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Images to Upload */}
              {newImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">New Photos (not uploaded yet)</p>
                  <div className="flex gap-3 flex-wrap mb-3">
                    {newImages.map((file, index) => (
                      <div key={index} className="relative group">
                        <div className="w-24 h-24 rounded-lg overflow-hidden border border-blue-300 bg-blue-50">
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={`New ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveNewImage(index)}
                          className="absolute -top-2 -right-2 bg-gray-500 text-white rounded-full p-1 hover:bg-gray-600 transition-colors shadow-md"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    onClick={handleUploadNewImages}
                    disabled={uploadingImages}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {uploadingImages ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload {newImages.length} New Photo{newImages.length > 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Add New Images */}
              {canAddMoreImages && (
                <div>
                  <label className="cursor-pointer">
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 flex flex-col items-center justify-center text-gray-400 hover:text-blue-500 transition-colors">
                      <Plus className="h-6 w-6" />
                      <span className="text-xs mt-1">Add Photo</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleNewImageSelect}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    You can add {5 - totalImageCount} more photo{5 - totalImageCount !== 1 ? 's' : ''}
                  </p>
                </div>
              )}

              {!canAddMoreImages && existingImages.length >= 5 && (
                <p className="text-sm text-amber-600">
                  Maximum 5 photos reached. Remove some to add new ones.
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                
                <div>
                  <Label htmlFor="title">Ad Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., iPhone 15 Pro Max - Like New"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    maxLength={200}
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your item in detail..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="condition">Condition *</Label>
                    <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Brand New">Brand New</SelectItem>
                        <SelectItem value="Like New">Like New</SelectItem>
                        <SelectItem value="Used">Used</SelectItem>
                        <SelectItem value="For Parts">For Parts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Price & Location */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Price & Location</h3>
                
                <div>
                  <Label htmlFor="price">Price ({currencySymbol}) *</Label>
                  <Input
                    id="price"
                    type="number"
                    placeholder="e.g., 450000"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    min="0"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="negotiable"
                    checked={formData.negotiable}
                    onCheckedChange={(checked) => setFormData({ ...formData, negotiable: checked })}
                  />
                  <Label htmlFor="negotiable" className="cursor-pointer">Price is negotiable</Label>
                </div>
              </div>

              {/* Location Section with Google Maps */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">📍 Location</h3>
                <GoogleLocationPicker 
                  value={locationData}
                  onChange={setLocationData}
                />
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Contact Information</h3>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+94771234567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">Buyers will contact you via this number</p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  disabled={saving}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditAd;
